/** java.awt.Rectangle / Polygon / geom.Point2D — faithful ports. */

export class Rectangle {
  x: number;
  y: number;
  width: number;
  height: number;

  constructor(x = 0, y = 0, width = 0, height = 0) {
    this.x = x;
    this.y = y;
    this.width = width;
    this.height = height;
  }

  /** Exact port of java.awt.Rectangle.intersects(Rectangle). */
  intersects(r: Rectangle): boolean {
    let w = this.width;
    let h = this.height;
    let rw = r.width;
    let rh = r.height;
    if (rw <= 0 || rh <= 0 || w <= 0 || h <= 0) return false;
    const tx = this.x;
    const ty = this.y;
    const rx = r.x;
    const ry = r.y;
    rw += rx;
    rh += ry;
    w += tx;
    h += ty;
    // overflow checks in Java are irrelevant here (doubles), keep the logical form
    return (rw < rx || rw > tx) && (rh < ry || rh > ty) && (w < tx || w > rx) && (h < ty || h > ry);
  }

  /** Exact port of java.awt.Rectangle.contains(int, int). */
  contains(x: number, y: number): boolean {
    const w = this.width;
    const h = this.height;
    if (w <= 0 || h <= 0) return false;
    if (x < this.x || y < this.y) return false;
    return x < this.x + w && y < this.y + h;
  }

  getBounds(): Rectangle {
    return new Rectangle(this.x, this.y, this.width, this.height);
  }

  /** Rectangle intersection (used by the clip stack). Returns an empty rect if disjoint. */
  intersection(r: Rectangle): Rectangle {
    const x1 = Math.max(this.x, r.x);
    const y1 = Math.max(this.y, r.y);
    const x2 = Math.min(this.x + this.width, r.x + r.width);
    const y2 = Math.min(this.y + this.height, r.y + r.height);
    return new Rectangle(x1, y1, Math.max(0, x2 - x1), Math.max(0, y2 - y1));
  }
}

export class Polygon {
  xpoints: number[] = [];
  ypoints: number[] = [];
  npoints = 0;

  constructor(xpoints?: number[], ypoints?: number[], npoints?: number) {
    if (xpoints && ypoints && npoints !== undefined) {
      this.xpoints = xpoints.slice(0, npoints);
      this.ypoints = ypoints.slice(0, npoints);
      this.npoints = npoints;
    }
  }

  addPoint(x: number, y: number): void {
    this.xpoints.push(x);
    this.ypoints.push(y);
    this.npoints++;
  }

  /** Even-odd crossing test, matching java.awt.Polygon.contains. */
  contains(x: number, y: number): boolean {
    if (this.npoints <= 2) return false;
    let hits = 0;
    let lastx = this.xpoints[this.npoints - 1];
    let lasty = this.ypoints[this.npoints - 1];

    for (let i = 0; i < this.npoints; i++) {
      const curx = this.xpoints[i];
      const cury = this.ypoints[i];

      if (cury === lasty) {
        lastx = curx;
        lasty = cury;
        continue;
      }

      let leftx: number;
      if (curx < lastx) {
        if (x >= lastx) {
          lastx = curx;
          lasty = cury;
          continue;
        }
        leftx = curx;
      } else {
        if (x >= curx) {
          lastx = curx;
          lasty = cury;
          continue;
        }
        leftx = lastx;
      }

      let test1: number;
      let test2: number;
      if (cury < lasty) {
        if (y < cury || y >= lasty) {
          lastx = curx;
          lasty = cury;
          continue;
        }
        if (x < leftx) {
          hits++;
          lastx = curx;
          lasty = cury;
          continue;
        }
        test1 = x - curx;
        test2 = y - cury;
      } else {
        if (y < lasty || y >= cury) {
          lastx = curx;
          lasty = cury;
          continue;
        }
        if (x < leftx) {
          hits++;
          lastx = curx;
          lasty = cury;
          continue;
        }
        test1 = x - lastx;
        test2 = y - lasty;
      }

      if (test1 < test2 / (lasty - cury) * (lastx - curx)) hits++;

      lastx = curx;
      lasty = cury;
    }

    return (hits & 1) !== 0;
  }

  getBounds(): Rectangle {
    if (this.npoints === 0) return new Rectangle();
    let minX = this.xpoints[0], maxX = this.xpoints[0];
    let minY = this.ypoints[0], maxY = this.ypoints[0];
    for (let i = 1; i < this.npoints; i++) {
      minX = Math.min(minX, this.xpoints[i]);
      maxX = Math.max(maxX, this.xpoints[i]);
      minY = Math.min(minY, this.ypoints[i]);
      maxY = Math.max(maxY, this.ypoints[i]);
    }
    return new Rectangle(minX, minY, maxX - minX, maxY - minY);
  }
}

export class Point2DFloat {
  constructor(public x: number, public y: number) {}
}
